import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, FileText, Pencil, Plus, Printer, UserPlus } from 'lucide-react';
import { Button, FormField, Modal } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { peutVoirTarifs } from '@/lib/permissions';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';
import { listChantiers } from '@/services/chantiers';
import { getEntreprise, jourReposJs } from '@/services/parametres';
import { createTiers, updateTiers, type TiersInput } from '@/services/tiers';
import { CATEGORIE_LABEL, ENGIN_CATEGORIES, type EnginCategorie, createEngin } from '@/services/engins';
import {
  type Decompte, type ModeGasoilEntrante, getDecompte, updateDecompte,
} from '@/services/decomptesLocation';
import {
  type EnginACompleter, type GrilleJour, type GrilleLigne,
  facturerLoueur, getGrilleLoueur, listEnginsACompleter, listLoueurs, modifierEnginLoue,
  ouvrirDecompteEngin, pointerEntrant,
} from '@/services/locationsEntrantes';
import { DecompteDetail } from './LocationTab';
import { FicheMensuelleModal } from './FicheMensuelleModal';
import type { FichePerimetre } from '@/services/etatsLocation';
import { type GasoilConfig, type PointageSortie, type PointageValeurs, PointageForm, PopoverPointage } from './PointageForm';

/* ------------------------------- Dates (samedi → vendredi, comme le Pointage) ---- */
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const samediSemaine = (d: Date) => addDays(d, -(((d.getDay() - 6) + 7) % 7));
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOUR_ABBR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const num = (s: string | null | undefined) => Number(s || 0);
const today = () => iso(new Date());
const selectCls = 'h-8 w-full rounded-md border border-border bg-surface-secondary px-2 text-xs';
const selectLg = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';

/** 1 → « 1 », 0,5 → « ½ », 0,75 → « ¾ », sinon décimale courte. */
function fraction(j: number): string {
  if (j === 0) return '—';
  if (j === 0.5) return '½';
  if (j === 0.75) return '¾';
  if (j === 0.25) return '¼';
  return String(Math.round(j * 100) / 100).replace('.', ',');
}

const GASOIL_COURT: Record<ModeGasoilEntrante, string> = {
  DEDUIT: 'Gasoil : moi, déduit',
  MA_CHARGE: 'Gasoil : à ma charge',
  LOUEUR: 'Gasoil : le loueur',
};
const GASOIL_LONG: Record<ModeGasoilEntrante, string> = {
  DEDUIT: "C'est moi qui le mets — déduit de ce que je dois au loueur",
  MA_CHARGE: "À ma charge d'emblée — rien à déduire, je suis mes frais",
  LOUEUR: 'Le loueur fournit son gasoil (non suivi)',
};

/** Pré-remplit le formulaire avec ce qui est déjà pointé ce jour-là (pour pouvoir le corriger). */
function valeursDepuis(j: GrilleJour | undefined, masque: boolean): Partial<PointageValeurs> {
  if (!j) return {};
  const litres = num(j.gasoil_litres) ? String(num(j.gasoil_litres)) : '';
  const montant = !masque && num(j.gasoil_montant) ? String(num(j.gasoil_montant)) : '';
  if (j.heure_debut && j.heure_fin) {
    return { presence: 'HORAIRES', debut: j.heure_debut, fin: j.heure_fin, motif: j.motif_perte ?? '', litres, montant };
  }
  const jr = num(j.journees);
  return {
    presence: jr === 0 ? 'AUCUNE' : jr === 0.5 ? 'DEMI' : 'PLEINE',
    heuresSup: num(j.heures_sup) ? String(num(j.heures_sup)) : '',
    litres, montant,
  };
}

type ModalEngin =
  | { mode: 'ajout' }
  | { mode: 'completer'; complete: EnginACompleter }
  | { mode: 'modifier'; ligne: GrilleLigne };

/**
 * Locations ENTRANTES vues par LOUEUR. Un loueur (le propriétaire, comme un sous-traitant)
 * possède N engins ; ils sont tous là, une ligne chacun, une case par jour. On clique la case
 * pour pointer — le décompte se crée tout seul — et la facture est unique par loueur et chantier.
 */
export function LocationsParLoueur() {
  const qc = useQueryClient();
  const voitTarifs = peutVoirTarifs(useAuthStore((s) => s.user));
  const [loueurId, setLoueurId] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [vue, setVue] = useState<'semaine' | 'mois'>('semaine');
  const [cursor, setCursor] = useState(() => new Date());
  const [menu, setMenu] = useState<{ ligne: GrilleLigne; date: string; x: number; y: number } | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [modalLoueur, setModalLoueur] = useState<'creer' | 'modifier' | null>(null);
  const [modalEngin, setModalEngin] = useState<ModalEngin | null>(null);
  const [fiche, setFiche] = useState<{ titre: string; perimetre: FichePerimetre } | null>(null);   // fiche de pointage mensuelle

  const dates = useMemo<Date[]>(() => {
    if (vue === 'semaine') { const s = samediSemaine(cursor); return Array.from({ length: 7 }, (_, i) => addDays(s, i)); }
    const y = cursor.getFullYear(), m = cursor.getMonth(), nb = new Date(y, m + 1, 0).getDate();
    return Array.from({ length: nb }, (_, i) => new Date(y, m, i + 1));
  }, [vue, cursor]);
  const dateFrom = iso(dates[0]);
  const dateTo = iso(dates[dates.length - 1]);

  const { data: loueurs } = useQuery({ queryKey: ['loueurs'], queryFn: listLoueurs });
  const { data: aCompleter } = useQuery({ queryKey: ['engins-a-completer'], queryFn: listEnginsACompleter });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'mini'], queryFn: () => listChantiers(), enabled: voitTarifs });
  const { data: entreprise } = useQuery({ queryKey: ['entreprise'], queryFn: getEntreprise });
  const { data: grille, isLoading } = useQuery({
    queryKey: ['grille-loueur', loueurId, dateFrom, dateTo],
    queryFn: () => getGrilleLoueur(loueurId as string, dateFrom, dateTo),
    enabled: !!loueurId,
  });
  const { data: detail } = useQuery({
    queryKey: ['decompte', detailId], queryFn: () => getDecompte(detailId as string), enabled: !!detailId,
  });

  const repos = jourReposJs(entreprise?.jour_repos_hebdo);
  const prixLitre = Number(entreprise?.prix_carburant_defaut ?? 0);
  const masque = !!grille?.montants_masques || !voitTarifs;
  const loueurCourant = (loueurs ?? []).find((l) => l.id === loueurId) ?? null;
  const listeChantiers = (chantiers?.items ?? []).map((c) => ({ id: c.id, label: `${c.code} · ${c.nom}` }));

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['grille-loueur'] });
    qc.invalidateQueries({ queryKey: ['loueurs'] });
    qc.invalidateQueries({ queryKey: ['engins-a-completer'] });
    qc.invalidateQueries({ queryKey: ['decompte'] });
    qc.invalidateQueries({ queryKey: ['decomptes'] });
  };
  const err = (e: unknown) => toast.error(getApiErrorMessage(e));

  const pointerM = useMutation({
    mutationFn: (v: { ligne: GrilleLigne; date: string; sortie: PointageSortie }) =>
      pointerEntrant({ engin_id: v.ligne.engin_id, chantier_id: v.ligne.chantier_id as string, date: v.date, ...v.sortie }),
    onSuccess: () => { rafraichir(); setMenu(null); },
    onError: (e) => { err(e); setMenu(null); },
  });
  const majDecompteM = useMutation({
    mutationFn: (v: { id: string; mode_gasoil?: ModeGasoilEntrante; chauffeur_nom?: string | null }) =>
      updateDecompte(v.id, { mode_gasoil: v.mode_gasoil, chauffeur_nom: v.chauffeur_nom }),
    onSuccess: rafraichir, onError: err,
  });
  // Choisir le chantier d'un engin = l'affecter : son décompte s'ouvre aussitôt (rien à « valider »).
  const affecterM = useMutation({
    mutationFn: (v: { ligne: GrilleLigne; chantierId: string }) =>
      ouvrirDecompteEngin({ engin_id: v.ligne.engin_id, chantier_id: v.chantierId, mode_gasoil: 'DEDUIT' }),
    onSuccess: () => { toast.success('Engin affecté au chantier.'); rafraichir(); }, onError: err,
  });
  const facturerM = useMutation({
    mutationFn: (v: { chantierId: string }) => facturerLoueur(loueurId as string, v.chantierId),
    onSuccess: (f) => {
      toast.success(`Facture ${f.facture_reference ?? 'brouillon'} générée : ${f.nb_decomptes} engin(s) cumulés.`);
      rafraichir();
    },
    onError: err,
  });

  const ouvrirMenu = (ligne: GrilleLigne, date: string, x: number, y: number) => {
    if (!ligne.decompte_id) { toast.error('Choisissez d’abord le chantier de cet engin.'); return; }
    if (!ligne.modifiable) { toast.error('Pointage validé ou facturé : rouvrez-le depuis le détail pour le corriger.'); return; }
    setMenu({ ligne, date, x, y });
  };
  const gasoilPour = (l: GrilleLigne): GasoilConfig | null => {
    const m = l.mode_gasoil ?? 'DEDUIT';
    if (m === 'LOUEUR') return null;       // gasoil du loueur : non suivi
    return {
      libelleMode: m === 'MA_CHARGE' ? 'à ma charge, non déduit au loueur' : 'déduit de ce que je dois au loueur',
      prixLitre, masque,
    };
  };

  const loueursFiltres = (loueurs ?? []).filter((l) => !recherche.trim() || l.raison_sociale.toLowerCase().includes(recherche.trim().toLowerCase()));
  const nav = (d: number) => setCursor((c) => (vue === 'semaine' ? addDays(c, d * 7) : new Date(c.getFullYear(), c.getMonth() + d, 1)));
  const sat = samediSemaine(cursor);
  const totalLitres = (grille?.lignes ?? []).reduce((a, l) => a + num(l.gasoil_litres), 0);
  const totalGasoilDA = (grille?.lignes ?? []).reduce((a, l) => a + num(l.gasoil_cout), 0);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
      {/* ───────── Colonne gauche : les loueurs ───────── */}
      <div className="space-y-3">
        {voitTarifs && <Button className="w-full" onClick={() => setModalLoueur('creer')}><UserPlus /> Nouveau loueur</Button>}
        <Input placeholder="Rechercher un loueur…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        <div className="space-y-1">
          {loueursFiltres.map((l) => (
            <button
              key={l.id} onClick={() => setLoueurId(l.id)}
              className={cn('w-full rounded-md border px-3 py-2 text-left text-sm transition-colors',
                loueurId === l.id ? 'border-brand-primary bg-brand-primary/5' : 'border-border hover:bg-surface-hover')}
            >
              <span className="block truncate font-medium text-content-primary">{l.raison_sociale}</span>
              <span className="block text-[11px] text-content-muted">
                {l.nb_engins} engin{l.nb_engins > 1 ? 's' : ''}
                {l.nb_decomptes_ouverts ? ` · ${l.nb_decomptes_ouverts} en cours` : ''}
              </span>
            </button>
          ))}
          {loueursFiltres.length === 0 && (
            <p className="rounded-md border border-border bg-surface-secondary px-3 py-6 text-center text-xs text-content-muted">
              Aucun loueur. Créez le propriétaire des engins que vous louez : ses engins lui seront rattachés.
            </p>
          )}
        </div>

        {(aCompleter ?? []).length > 0 && voitTarifs && (
          <div className="rounded-lg border border-status-working/40 bg-status-working/5 p-3">
            <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-content-primary">
              <AlertTriangle className="size-4" /> À compléter ({aCompleter?.length})
            </p>
            <p className="mb-2 text-[11px] text-content-muted">Engins loués sans loueur ou sans prix : impossible de les pointer.</p>
            {aCompleter?.map((e) => (
              <button key={e.id} onClick={() => setModalEngin({ mode: 'completer', complete: e })}
                className="mb-1 block w-full rounded border border-border bg-surface-secondary px-2 py-1.5 text-left text-xs hover:bg-surface-hover">
                <span className="font-medium">{e.code} · {e.designation}</span>
                <span className="block text-[10px] text-content-muted">
                  {[e.manque_loueur && 'loueur manquant', e.manque_tarif && 'prix manquant'].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ───────── Colonne droite : les engins du loueur ───────── */}
      <div className="min-w-0">
        {!loueurId ? (
          <p className="py-16 text-center text-sm text-content-muted">
            Choisissez un loueur pour pointer tous ses engins, ou créez-en un nouveau.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-secondary p-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 truncate text-base font-bold text-content-primary">
                  {grille?.raison_sociale ?? '…'}
                  {voitTarifs && (
                    <button onClick={() => setModalLoueur('modifier')} title="Modifier le loueur" aria-label="Modifier le loueur"
                      className="rounded p-1 text-content-muted hover:bg-surface-hover hover:text-brand-primary">
                      <Pencil className="size-4" />
                    </button>
                  )}
                </p>
                <p className="text-xs text-content-muted">
                  {grille?.lignes.length ?? 0} engin(s) · propriétaire{loueurCourant?.telephone ? ` · ${loueurCourant.telephone}` : ''}
                </p>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-md border border-border p-0.5">
                  {(['semaine', 'mois'] as const).map((v) => (
                    <button key={v} onClick={() => setVue(v)}
                      className={cn('rounded px-3 py-1 text-sm font-medium', vue === v ? 'bg-brand-accent text-brand-accent-contrast' : 'text-content-secondary hover:bg-surface-hover')}>
                      {v === 'semaine' ? 'Hebdomadaire' : 'Mensuel'}
                    </button>
                  ))}
                </div>
                <Button variant="secondary" size="sm" onClick={() => nav(-1)} aria-label="Précédent"><ChevronLeft className="size-4" /></Button>
                <span className="min-w-[150px] text-center text-sm font-semibold">
                  {vue === 'mois' ? `${MOIS[cursor.getMonth()]} ${cursor.getFullYear()}`
                    : `${pad(sat.getDate())}/${pad(sat.getMonth() + 1)} → ${pad(addDays(sat, 6).getDate())}/${pad(addDays(sat, 6).getMonth() + 1)}`}
                </span>
                <Button variant="secondary" size="sm" onClick={() => nav(1)} aria-label="Suivant"><ChevronRight className="size-4" /></Button>
                <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {voitTarifs && <Button size="sm" onClick={() => setModalEngin({ mode: 'ajout' })}><Plus className="size-4" /> Ajouter un engin à ce loueur</Button>}
              {(grille?.lignes.length ?? 0) > 0 && (
                <Button variant="secondary" size="sm"
                  onClick={() => setFiche({ titre: `Tous les engins de ${grille?.raison_sociale ?? 'ce loueur'}`, perimetre: { loueur_id: loueurId as string } })}>
                  <Printer className="size-4" /> Fiches du mois (tous les engins)
                </Button>
              )}
              {!masque && (grille?.a_facturer ?? []).map((a) => (
                <Button key={a.chantier_id} variant="secondary" size="sm" loading={facturerM.isPending}
                  onClick={() => { if (window.confirm(`Générer UNE facture pour ${grille?.raison_sociale} sur ${a.chantier_libelle} : ${a.nb_decomptes} engin(s), net ${formatDZD(num(a.net))} ?`)) facturerM.mutate({ chantierId: a.chantier_id }); }}>
                  <FileText className="size-4" /> Facturer {a.chantier_libelle.split(' · ')[0]} · {a.nb_decomptes} engin(s) · {formatDZD(num(a.net))}
                </Button>
              ))}
            </div>

            {isLoading ? (
              <div className="rounded-lg border border-border bg-surface-secondary p-12 text-center text-sm text-content-muted">Chargement…</div>
            ) : (grille?.lignes.length ?? 0) === 0 ? (
              <div className="rounded-lg border border-border bg-surface-secondary p-12 text-center text-sm text-content-muted">
                Ce loueur n'a encore aucun engin.{voitTarifs ? ' Cliquez « Ajouter un engin à ce loueur ».' : ''}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border bg-surface-secondary shadow-sm">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-primary/40 text-content-secondary">
                      <th className="sticky left-0 z-10 w-[300px] min-w-[300px] bg-surface-secondary px-3 py-2 text-left text-[11px] font-semibold uppercase text-content-muted">
                        Engin ({grille?.lignes.length})
                      </th>
                      {dates.map((d) => (
                        <th key={iso(d)} className={cn('px-1 py-1.5 text-center text-xs font-semibold', vue === 'mois' ? 'min-w-[34px]' : 'min-w-[68px]',
                          d.getDay() === repos && 'bg-status-stuck/10 text-status-stuck')}>
                          <div>{JOUR_ABBR[d.getDay()]}{d.getDay() === repos && vue === 'semaine' ? ' · repos' : ''}</div>
                          <div className="text-[11px] font-normal text-content-muted">{vue === 'mois' ? d.getDate() : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`}</div>
                        </th>
                      ))}
                      <th className="bg-brand-primary/10 px-2 text-center text-[11px] font-semibold uppercase text-brand-primary">Jours</th>
                      <th className="px-2 text-center text-[11px] font-semibold uppercase text-content-muted">⛽ Gasoil</th>
                      {!masque && <th className="px-2 text-right text-[11px] font-semibold uppercase text-content-muted">Brut</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {grille?.lignes.map((l) => {
                      const parJour = new Map<string, GrilleJour>(l.jours.map((j) => [j.date, j]));
                      const typeMachine = [l.categorie && CATEGORIE_LABEL[l.categorie as EnginCategorie], l.marque, l.modele].filter(Boolean).join(' · ');
                      return (
                        <tr key={`${l.engin_id}-${l.decompte_id ?? 'x'}`} className="border-b border-border/60 align-top last:border-0">
                          <td className="sticky left-0 z-10 bg-surface-secondary px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate font-medium text-content-primary">{l.engin_libelle}</span>
                              {!l.modifiable && <span title="Pointage validé / facturé — lecture seule">🔒</span>}
                              {l.alerte_surconso && <span title="Surconsommation de gasoil"><AlertTriangle className="size-4 text-status-stuck" /></span>}
                              <span className="ml-auto flex shrink-0 gap-0.5">
                                <button onClick={() => setFiche({ titre: `${l.engin_code} · ${l.engin_libelle}`, perimetre: { engin_id: l.engin_id } })}
                                  title="Imprimer la fiche de pointage mensuelle de cette machine" aria-label="Imprimer la fiche de pointage mensuelle"
                                  className="rounded p-1 text-content-muted hover:bg-surface-hover hover:text-brand-primary">
                                  <Printer className="size-3.5" />
                                </button>
                                <button onClick={() => setModalEngin({ mode: 'modifier', ligne: l })} title="Modifier cette machine (nom, chauffeur, chantier, prix…)"
                                  aria-label="Modifier cette machine"
                                  className="rounded p-1 text-content-muted hover:bg-surface-hover hover:text-brand-primary">
                                  <Pencil className="size-3.5" />
                                </button>
                              </span>
                            </div>
                            <div className="truncate text-[10px] text-content-muted">{l.engin_code}{typeMachine ? ` · ${typeMachine}` : ''}</div>
                            <div className="mt-1.5 space-y-1">
                              {l.decompte_id ? (
                                <p className="text-[11px] text-content-secondary">📍 {l.chantier_libelle}</p>
                              ) : !voitTarifs ? (
                                <p className="text-[11px] italic text-content-muted">Pas encore affecté à un chantier (à définir par le responsable).</p>
                              ) : (
                                <select className={selectCls} value="" disabled={affecterM.isPending}
                                  onChange={(e) => { if (e.target.value) affecterM.mutate({ ligne: l, chantierId: e.target.value }); }}>
                                  <option value="">Choisir le chantier de cet engin…</option>
                                  {listeChantiers.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                </select>
                              )}
                              {l.decompte_id && (
                                <>
                                  <select className={selectCls} value={l.mode_gasoil ?? 'DEDUIT'} disabled={!l.modifiable}
                                    onChange={(e) => majDecompteM.mutate({ id: l.decompte_id as string, mode_gasoil: e.target.value as ModeGasoilEntrante })}>
                                    {(Object.keys(GASOIL_COURT) as ModeGasoilEntrante[]).map((m) => <option key={m} value={m}>{GASOIL_COURT[m]}</option>)}
                                  </select>
                                  <input className={cn(selectCls, 'placeholder:text-content-muted')} placeholder="👤 Chauffeur (du loueur)"
                                    disabled={!l.modifiable} defaultValue={l.chauffeur_nom ?? ''}
                                    key={`${l.decompte_id}-${l.chauffeur_nom ?? ''}`}
                                    onBlur={(e) => {
                                      const v = e.target.value.trim();
                                      if (v !== (l.chauffeur_nom ?? '')) majDecompteM.mutate({ id: l.decompte_id as string, chauffeur_nom: v || null });
                                    }} />
                                  <button className="text-[11px] font-medium text-brand-primary hover:underline" onClick={() => setDetailId(l.decompte_id)}>
                                    Détail · gasoil · avances
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                          {dates.map((d) => {
                            const j = parJour.get(iso(d));
                            const jr = num(j?.journees), hs = num(j?.heures_sup), perdues = num(j?.heures_perdues);
                            const litres = num(j?.gasoil_litres);
                            const vide = !j || (jr === 0 && hs === 0);
                            const argent = !masque && num(j?.gasoil_montant) ? ` = ${formatDZD(num(j?.gasoil_montant))}` : '';
                            return (
                              <td key={iso(d)}
                                onClick={(ev) => ouvrirMenu(l, iso(d), ev.clientX, ev.clientY)}
                                title={j ? `${iso(d)} · ${jr} j${hs ? ` + ${hs} h sup` : ''}${perdues ? ` · ${perdues} h perdue(s)` : ''}${j.heure_debut ? ` · ${j.heure_debut}→${j.heure_fin}` : ''}${litres ? ` · ⛽ ${litres} L${argent}` : ''}` : 'Cliquer pour pointer'}
                                className={cn('cursor-pointer p-0.5 text-center hover:bg-brand-primary/10', d.getDay() === repos && 'bg-status-stuck/5')}>
                                {vide ? (
                                  litres ? <span className="text-[10px] text-content-muted">⛽{litres}L</span> : <span className="text-content-muted/40">·</span>
                                ) : (
                                  <div className={cn('mx-auto rounded px-1 py-0.5 text-xs font-semibold tabular-nums',
                                    perdues > 0 ? 'bg-status-working/20 text-content-primary' : 'bg-status-done/15 text-status-done',
                                    d.getDay() === repos && 'ring-1 ring-status-stuck/60')}>
                                    <div>{fraction(jr)}</div>
                                    {hs > 0 && <div className="text-[9px] font-normal">+{hs}h sup</div>}
                                    {perdues > 0 && <div className="text-[9px] font-normal">−{perdues}h</div>}
                                    {litres > 0 && <div className="text-[9px] font-normal">⛽{litres}L</div>}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td className="bg-brand-primary/5 px-2 py-2 text-center font-semibold tabular-nums text-brand-primary">{num(l.total_jours) ? fraction(num(l.total_jours)) : ''}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-center text-xs tabular-nums text-content-secondary">
                            {num(l.gasoil_litres) ? <>{num(l.gasoil_litres)} L{!masque && num(l.gasoil_cout) ? <span className="block text-[10px] text-content-muted">{formatDZD(num(l.gasoil_cout))}</span> : null}</> : ''}
                          </td>
                          {!masque && <td className="whitespace-nowrap px-2 py-2 text-right font-semibold tabular-nums">{num(l.total_brut) ? formatDZD(num(l.total_brut)) : ''}</td>}
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-border bg-surface-primary/50 text-xs font-semibold">
                      <td className="sticky left-0 z-10 bg-surface-primary/50 px-3 py-1.5 uppercase text-content-muted">Total du loueur</td>
                      {dates.map((d) => {
                        const t = (grille?.lignes ?? []).reduce((a, l) => a + num(l.jours.find((x) => x.date === iso(d))?.journees), 0);
                        return <td key={iso(d)} className="px-1 py-1.5 text-center tabular-nums text-content-secondary">{t ? fraction(t) : ''}</td>;
                      })}
                      <td className="bg-brand-primary/10 px-2 text-center tabular-nums text-brand-primary">{fraction(num(grille?.total_jours))}</td>
                      <td className="whitespace-nowrap px-2 text-center tabular-nums">
                        {totalLitres ? <>{totalLitres} L{!masque && totalGasoilDA ? <span className="block text-[10px] font-normal text-content-muted">{formatDZD(totalGasoilDA)}</span> : null}</> : ''}
                      </td>
                      {!masque && <td className="whitespace-nowrap px-2 text-right tabular-nums">{formatDZD(num(grille?.total_brut))}</td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-content-muted">
              Cliquez une case pour pointer l'engin ce jour-là : présence, heures supplémentaires et gasoil mis. Le décompte se crée tout seul ; la facture est unique pour le loueur et le chantier.
              {masque && ' Votre profil permet de pointer ; les montants ne vous sont pas communiqués.'}
            </p>
          </div>
        )}
      </div>

      {/* ───────── Formulaire de pointage d'une case ───────── */}
      {menu && (
        <PopoverPointage x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <PointageForm
            key={`${menu.ligne.decompte_id}-${menu.date}`}
            titre={`${menu.ligne.engin_code} · ${menu.date.slice(8, 10)}/${menu.date.slice(5, 7)}/${menu.date.slice(0, 4)}`}
            enRepos={new Date(`${menu.date}T00:00:00`).getDay() === repos}
            horaires
            gasoil={gasoilPour(menu.ligne)}
            initial={valeursDepuis(menu.ligne.jours.find((x) => x.date === menu.date), masque)}
            jourAvecGasoil={num(menu.ligne.jours.find((x) => x.date === menu.date)?.gasoil_litres) > 0}
            enCours={pointerM.isPending}
            onEnregistrer={(sortie) => pointerM.mutate({ ligne: menu.ligne, date: menu.date, sortie })}
            onEffacer={() => {
              const avecGasoil = !!gasoilPour(menu.ligne) && num(menu.ligne.jours.find((x) => x.date === menu.date)?.gasoil_litres) > 0;
              pointerM.mutate({
                ligne: menu.ligne, date: menu.date,
                sortie: { journees: '0', heures_sup: '0', ...(avecGasoil ? { gasoil_litres: '0', gasoil_avance: '0' } : {}) },
              });
            }}
          />
        </PopoverPointage>
      )}

      {/* ───────── Détail d'un engin : gasoil, avances, validation ───────── */}
      <Modal open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} title="Détail de la location de l'engin" size="xl">
        {detail ? <DecompteDetail decompte={detail as Decompte} onChange={rafraichir} /> : <p className="py-8 text-center text-sm text-content-muted">Chargement…</p>}
      </Modal>

      {fiche && <FicheMensuelleModal titre={fiche.titre} perimetre={fiche.perimetre} moisInitial={cursor} onClose={() => setFiche(null)} />}

      {modalLoueur && (
        <LoueurModal
          loueur={modalLoueur === 'modifier' && loueurCourant ? loueurCourant : null}
          onClose={() => setModalLoueur(null)}
          onSaved={(id) => { setLoueurId(id); rafraichir(); }}
        />
      )}
      {modalEngin && (
        <EnginModal
          modal={modalEngin} loueurId={loueurId} loueurs={loueurs ?? []} chantiers={listeChantiers} voitTarifs={voitTarifs}
          onCreerLoueur={() => { setModalEngin(null); setModalLoueur('creer'); }}
          onClose={() => setModalEngin(null)}
          onDone={(lid) => { if (lid) setLoueurId(lid); rafraichir(); }}
        />
      )}
    </div>
  );
}

/* ----------------------------------------- Loueur : créer / modifier ---------- */
function LoueurModal({ loueur, onClose, onSaved }: {
  loueur: { id: string; raison_sociale: string; telephone: string | null } | null;
  onClose: () => void; onSaved: (id: string) => void;
}) {
  const [nom, setNom] = useState(loueur?.raison_sociale ?? '');
  const [tel, setTel] = useState(loueur?.telephone ?? '');
  const m = useMutation({
    mutationFn: async () => {
      if (loueur) {
        await updateTiers(loueur.id, { raison_sociale: nom.trim(), telephone: tel.trim() || null });
        return loueur.id;
      }
      const t = await createTiers({
        code: '', type: 'FOURNISSEUR', raison_sociale: nom.trim(), telephone: tel.trim() || null,
        specialite: "Location d'engins",
      } as TiersInput);
      return t.id;
    },
    onSuccess: (id) => { toast.success(loueur ? 'Loueur modifié.' : 'Loueur créé.'); onClose(); onSaved(id); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={loueur ? 'Modifier le loueur' : 'Nouveau loueur'} size="sm"
      footer={<><Button variant="secondary" onClick={onClose}>Annuler</Button>
        <Button disabled={!nom.trim()} loading={m.isPending} onClick={() => m.mutate()}>{loueur ? 'Enregistrer' : 'Créer le loueur'}</Button></>}>
      <div className="space-y-3">
        {!loueur && <p className="text-xs text-content-muted">Le propriétaire des engins que vous louez — une personne ou une société. Ses engins lui seront rattachés.</p>}
        <FormField label="Nom ou raison sociale" required><Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="ex. Ets Benali" /></FormField>
        <FormField label="Téléphone"><Input value={tel} onChange={(e) => setTel(e.target.value)} /></FormField>
      </div>
    </Modal>
  );
}

/* ----------------------------------------- Engin : ajouter / compléter / modifier ---- */
function EnginModal({ modal, loueurId, loueurs, chantiers, voitTarifs, onCreerLoueur, onClose, onDone }: {
  modal: ModalEngin; loueurId: string | null;
  loueurs: { id: string; raison_sociale: string }[]; chantiers: { id: string; label: string }[];
  voitTarifs: boolean; onCreerLoueur: () => void; onClose: () => void; onDone: (loueurId?: string) => void;
}) {
  const ligne = modal.mode === 'modifier' ? modal.ligne : null;
  const complete = modal.mode === 'completer' ? modal.complete : null;
  const decompteEditable = !!ligne?.decompte_id && !!ligne?.modifiable;
  const [f, setF] = useState({
    designation: ligne?.engin_libelle ?? '',
    categorie: ligne?.categorie ?? 'ENGIN_TP',
    marque: ligne?.marque ?? '', modele: ligne?.modele ?? '', immatriculation: ligne?.immatriculation ?? '',
    loueur: loueurId ?? '',
    tarif: ligne && num(ligne.tarif_jour) ? String(num(ligne.tarif_jour)) : '',
    chantier: ligne?.chantier_id ?? '',
    gasoil: (ligne?.mode_gasoil ?? 'DEDUIT') as ModeGasoilEntrante,
    chauffeur: ligne?.chauffeur_nom ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  const m = useMutation({
    mutationFn: async () => {
      if (complete) {
        await modifierEnginLoue(complete.id, { loueur_id: f.loueur, cout_location_jour: Number(f.tarif) });
        return f.loueur;
      }
      if (ligne) {
        await modifierEnginLoue(ligne.engin_id, {
          designation: f.designation.trim(), categorie: f.categorie, marque: f.marque, modele: f.modele,
          immatriculation: f.immatriculation, loueur_id: f.loueur || undefined,
          ...(voitTarifs && Number(f.tarif) > 0 ? { cout_location_jour: Number(f.tarif) } : {}),
          ...(decompteEditable
            ? { decompte_id: ligne.decompte_id as string, chantier_id: f.chantier || undefined, chauffeur_nom: f.chauffeur, mode_gasoil: f.gasoil }
            : {}),
        });
        return f.loueur;
      }
      const engin = await createEngin({
        code: '', designation: f.designation.trim(), categorie: f.categorie as EnginCategorie,
        marque: f.marque || null, modele: f.modele || null, immatriculation: f.immatriculation || null,
        type: 'LOUE', statut: 'DISPONIBLE', unite_compteur: 'H', loueur_id: f.loueur,
        cout_location_jour: Number(f.tarif), location_date_debut: today(),
      } as never);
      if (f.chantier) await ouvrirDecompteEngin({ engin_id: engin.id, chantier_id: f.chantier, mode_gasoil: f.gasoil, chauffeur_nom: f.chauffeur || null });
      return f.loueur;
    },
    onSuccess: (lid) => {
      toast.success(ligne ? 'Machine modifiée.' : complete ? 'Engin complété.' : 'Engin ajouté à ce loueur.');
      onClose(); onDone(lid);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const prixOk = (voitTarifs || complete || modal.mode === 'ajout') ? Number(f.tarif) > 0 : true;
  const ok = complete
    ? !!f.loueur && Number(f.tarif) > 0
    : !!f.designation.trim() && !!f.loueur && prixOk;
  const titre = ligne ? `Modifier ${ligne.engin_code} · ${ligne.engin_libelle}`
    : complete ? `Compléter ${complete.code} · ${complete.designation}` : 'Ajouter un engin à ce loueur';

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} size="lg" title={titre}
      footer={<><Button variant="secondary" onClick={onClose}>Annuler</Button>
        <Button disabled={!ok} loading={m.isPending} onClick={() => m.mutate()}>Enregistrer</Button></>}>
      {loueurs.length === 0 && (
        <div className="mb-3 rounded-md border border-status-working/40 bg-status-working/10 p-3 text-sm">
          <p className="mb-2 text-content-primary">Aucun loueur n'existe encore : créez d'abord le propriétaire de cet engin.</p>
          <Button size="sm" onClick={onCreerLoueur}><UserPlus className="size-4" /> Créer un loueur</Button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {!complete && (
          <>
            <FormField label="Désignation" required className="sm:col-span-2"><Input value={f.designation} onChange={(e) => set('designation', e.target.value)} placeholder="ex. Pelle mécanique sur chenilles" /></FormField>
            <FormField label="Type de machine">
              <select className={selectLg} value={f.categorie} onChange={(e) => set('categorie', e.target.value)}>
                {ENGIN_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>)}
              </select>
            </FormField>
            <FormField label="Immatriculation"><Input value={f.immatriculation} onChange={(e) => set('immatriculation', e.target.value)} /></FormField>
            <FormField label="Marque"><Input value={f.marque} onChange={(e) => set('marque', e.target.value)} placeholder="ex. Daewoo" /></FormField>
            <FormField label="Modèle"><Input value={f.modele} onChange={(e) => set('modele', e.target.value)} placeholder="ex. DH 360" /></FormField>
          </>
        )}
        <FormField label="Propriétaire (loueur)" required>
          <select className={selectLg} value={f.loueur} onChange={(e) => set('loueur', e.target.value)}>
            <option value="">— Sélectionner —</option>
            {loueurs.map((l) => <option key={l.id} value={l.id}>{l.raison_sociale}</option>)}
          </select>
        </FormField>
        {(voitTarifs || complete) && (
          <FormField label="Prix de location / jour (DA)" required hint={ligne ? 'Corrige aussi les décomptes encore ouverts' : undefined}>
            <Input type="number" min="1" value={f.tarif} onChange={(e) => set('tarif', e.target.value)} />
          </FormField>
        )}
        {!complete && (modal.mode === 'ajout' || decompteEditable) && (
          <>
            <FormField label="Chantier" hint={modal.mode === 'ajout' ? 'Facultatif — vous pourrez le choisir dans la grille'
              : 'Déplace aussi les jours déjà pointés de ce décompte'}>
              <select className={selectLg} value={f.chantier} onChange={(e) => set('chantier', e.target.value)}>
                <option value="">{modal.mode === 'ajout' ? '— Plus tard —' : '— Sélectionner —'}</option>
                {chantiers.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </FormField>
            {(modal.mode === 'modifier' || !!f.chantier) && (
              <>
                <FormField label="Gasoil de cet engin" className="sm:col-span-2">
                  <select className={selectLg} value={f.gasoil} onChange={(e) => set('gasoil', e.target.value)}>
                    {(Object.keys(GASOIL_LONG) as ModeGasoilEntrante[]).map((g) => <option key={g} value={g}>{GASOIL_LONG[g]}</option>)}
                  </select>
                </FormField>
                <FormField label="Chauffeur (toujours à la charge du loueur)" className="sm:col-span-2">
                  <Input value={f.chauffeur} onChange={(e) => set('chauffeur', e.target.value)} placeholder="Nom du conducteur" />
                </FormField>
              </>
            )}
          </>
        )}
        {modal.mode === 'ajout' && !f.chantier && (
          <p className="text-[11px] text-content-muted sm:col-span-2">
            Choisissez un chantier pour renseigner aussi le gasoil et le chauffeur ; sinon vous les réglerez sur la ligne de l'engin, une fois le chantier choisi.
          </p>
        )}
        {ligne && !decompteEditable && ligne.decompte_id && (
          <p className="text-[11px] text-content-muted sm:col-span-2">
            Cet engin est validé ou facturé : rouvrez son pointage (Détail) pour changer son chantier, son chauffeur ou son gasoil.
          </p>
        )}
      </div>
    </Modal>
  );
}
